import React from 'react';
import DOMPurify from 'dompurify';
import { useTheme } from '@mui/material/styles';
import { Box, InputLabel } from '@mui/material';
import { TextStyleKit } from '@tiptap/extension-text-style';
import { useEditor, EditorContent, useEditorState } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';

import RichTextEditorMenu from './RichTextEditorMenu';
import './richTextEditor.css';

type RichTextEditorProps = {
  label: React.ReactNode | undefined;
  rows?: string | number;
  value: unknown;
  handleChange: (value: unknown) => void;
  handleBlur?: (value: unknown) => void;
};

const extensions = [StarterKit, TextStyleKit];

function RichTextEditor(props: RichTextEditorProps) {
  const { label, rows = 1, value, handleChange, handleBlur } = props;
  const theme = useTheme();

  const editor = useEditor({
    immediatelyRender: true,
    extensions,
    content: `${value}`,
    onUpdate: ({ editor }) => {
      const html = DOMPurify.sanitize(editor.getHTML());
      handleChange(html);
    },
    onBlur: ({ editor }) => {
      if (handleBlur !== null && handleBlur !== undefined) {
        const html = DOMPurify.sanitize(editor.getHTML());
        handleBlur(html);
      }
    },
  });

  const editorState = useEditorState<{ isFocused: boolean }>({
    editor,
    selector: ({ editor }) => {
      return {
        isFocused: editor.isFocused,
      };
    },
  });

  const convertedRows = isNaN(Number(rows)) ? 1 : Number(rows);
  const height = convertedRows * 1.3 * 16;

  return (
    <Box>
      <InputLabel focused={editorState.isFocused}>{label}</InputLabel>
      <RichTextEditorMenu editor={editor} theme={theme} />
      <EditorContent
        editor={editor}
        style={{
          height: `${height}px`,
          padding: '7px',
          overflowY: 'auto',
          borderLeft: '1px solid #E7E7E7',
          borderRight: '1px solid #E7E7E7',
          borderBottom: '1px solid #E7E7E7',
          borderBottomLeftRadius: '5px',
          borderBottomRightRadius: '5px',
        }}
      />
    </Box>
  );
}

export type { RichTextEditorProps };

export default RichTextEditor;
